import { LoggerService } from '@angular-ru/cdk/logger';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { IDataboxContainer } from 'src/app/api/modules/core/dynamic/databoxes/IDataboxContainer';
import { DynamicContentDirective } from 'src/app/modules/shared/directives/dynamic-content/dynamic-content.directive';
import { DataboxAttemptsIconComponent } from '../databox-attempts-icon/databox-attempts-icon.component';
import { DataboxHighScoreComponent } from '../databox-highscore/databox-highscore.component';
import { DataboxLivesIconComponent } from '../databox-lives-icon/databox-lives-icon.component';
import { DataboxProgressionIconComponent } from '../databox-progression-icon/databox-progression-icon.component';
import { DataboxProgressionNumericComponent } from '../databox-progression-numeric/databox-progression-numeric.component';
import { DataboxScoreComponent } from '../databox-score/databox-score.component';
import { DataboxTimerComponent } from '../databox-timer/databox-timer.component';
import { DataboxContainerFactoryArray, DataboxContainerSupportedComponentReferences } from './databox-container.factory';

/**
 * Renders a server-configured row of databoxes (score, high score, timer,
 * progression, lives, attempts) created dynamically from `componentArray`.
 *
 * Nothing renders until a truthy config with a non-empty `componentArray`
 * arrives; when the config changes, the previously created databoxes are
 * destroyed before the new ones are created.
 *
 * Hosts (the plugin, via a `#databox` view child) push live game values into
 * the created databoxes through the `*Update` methods — each method targets
 * every instance of its databox type.
 *
 * See {@link IDataboxContainer} for the configuration structure.
 */
@Component({
  selector: 'app-databox-container',
  templateUrl: './databox-container.component.html',
  styleUrl: './databox-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DynamicContentDirective],
})
export class DataboxContainerComponent {
  private readonly loggerService = inject(LoggerService);

  /**
   * The anchor the databoxes are dynamically created into.
   */
  private readonly contentContainer = viewChild(DynamicContentDirective);

  /**
   * The Config as provided to the element.
   *
   * Consumers may bind `null` / `undefined` sources (e.g. `| async`); the
   * container simply renders nothing until a usable config arrives.
   */
  readonly config = input<IDataboxContainer | null>();

  /**
   * The validated databox configurations, or a warning when the config can't
   * be rendered (logged by an effect; the container then renders nothing).
   */
  private readonly viewConfig = computed(() => {
    const configuration = this.config();
    if (!configuration) {
      return undefined;
    }

    const componentArray = configuration.componentArray ?? [];
    if (componentArray.length === 0) {
      return { components: undefined, warning: 'Databox containers must be configured with a non-empty "componentArray".' };
    }

    return { components: componentArray, warning: undefined };
  });

  /**
   * Whether the wrapper element (and therefore the databox row) renders.
   */
  protected readonly hasComponents = computed(() => Boolean(this.viewConfig()?.components));

  /**
   * References to the dynamically created databoxes, for the update API and
   * teardown.
   */
  private readonly componentRefs = signal<DataboxContainerSupportedComponentReferences[]>([]);

  constructor() {
    // surface configuration problems (once per config change)
    effect(() => {
      const warning = this.viewConfig()?.warning;
      if (warning) {
        this.loggerService.warn(`[DataboxContainer] - ${warning}`);
      }
    });

    // (re)create the databoxes whenever the anchor or the configs change
    effect(() => {
      const container = this.contentContainer();
      const components = this.viewConfig()?.components;

      untracked(() => this.componentRefs()).forEach((ref) => ref.destroy());
      this.componentRefs.set(container && components ? DataboxContainerFactoryArray(container.viewContainerRef, components) : []);
    });

    inject(DestroyRef).onDestroy(() => {
      this.componentRefs().forEach((ref) => ref.destroy());
    });
  }

  ///////////////////////////////////////////////////////////////////////////
  // API
  ///////////////////////////////////////////////////////////////////////////

  /**
   * Updates the score for all DataboxScoreComponent instances.
   *
   * @param value - The new score value to be set.
   */
  scoreUpdate(value: number) {
    for (const item of this.componentRefs()) {
      if (item.instance instanceof DataboxScoreComponent) {
        item.instance.update(value);
      }
    }
  }

  /**
   * Updates the high score for all DataboxHighScoreComponent instances.
   *
   * @param value - The new high score value to be set.
   */
  highScoreUpdate(value: number) {
    for (const item of this.componentRefs()) {
      if (item.instance instanceof DataboxHighScoreComponent) {
        item.instance.update(value);
      }
    }
  }

  /**
   * Updates the timer for all DataboxTimerComponent instances.
   *
   * @param value - The new time value to be set.
   */
  timeUpdate(value: number) {
    for (const item of this.componentRefs()) {
      if (item.instance instanceof DataboxTimerComponent) {
        item.instance.update(value);
      }
    }
  }

  /**
   * Updates the numeric progression for all DataboxProgressionNumericComponent instances.
   *
   * @param current - The current progress value.
   * @param total - The total progress value, optional.
   */
  progressNumericUpdate(current: number, total?: number) {
    for (const item of this.componentRefs()) {
      if (item.instance instanceof DataboxProgressionNumericComponent) {
        item.instance.update(current, total);
      }
    }
  }

  /**
   * Updates the icon progression for all DataboxProgressionIconComponent instances.
   *
   * @param current - The current progress value.
   * @param total - The total progress value, optional.
   */
  progressIconUpdate(current: number, total?: number) {
    for (const item of this.componentRefs()) {
      if (item.instance instanceof DataboxProgressionIconComponent) {
        item.instance.update(current, total);
      }
    }
  }

  /**
   * Updates the lives icon for all DataboxLivesIconComponent instances.
   *
   * @param remainingLives - The number of remaining lives to be set.
   */
  livesIconUpdate(remainingLives: number) {
    for (const item of this.componentRefs()) {
      if (item.instance instanceof DataboxLivesIconComponent) {
        item.instance.update(remainingLives);
      }
    }
  }

  /**
   * Updates the attempts icon for all DataboxAttemptsIconComponent instances.
   *
   * @param stateList - The list of states to be set for the attempts.
   */
  attemptsIconUpdate(stateList: string[]) {
    for (const item of this.componentRefs()) {
      if (item.instance instanceof DataboxAttemptsIconComponent) {
        item.instance.update(stateList);
      }
    }
  }
}
